import "./propertyMap.css";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Empty, Space, Tag, Typography } from "antd";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { collection, getDocs } from "firebase/firestore";

import { db } from "../../firebase";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../helper/googleMaps";
import { RedNotify } from "../../helper/helper";
import { statusColor, statusLabel, normalizeStatus } from "../../helper/statusMeta";
import { isValidCoordinates } from "../../helper/coordinates";
import Loader from "../../components/loader/loader";

const { Text } = Typography;

// Default center (continental US) used only when no markers carry coordinates.
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

// Google's stock pin colors keyed by normalized status. Pending = red,
// completed = green, in-progress = amber. The colored pins double as the
// legend below.
const MARKER_COLORS = {
  pending: "red",
  "in progress": "yellow",
  completed: "green",
};

const markerIcon = (status) => {
  const color = MARKER_COLORS[normalizeStatus(status)] || "red";
  return `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
};

// A valid {lat, lng} pair with finite numeric values.
const hasCoordinates = (task) => isValidCoordinates(task?.coordinates);

const PropertyMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);

  const [loadingProperties, setLoadingProperties] = useState(true);
  const [properties, setProperties] = useState([]);
  // propertyId -> latest inspection status. Loaded after the pins render, so
  // the map doesn't wait on the (much larger) inspections collection.
  const [statusByProperty, setStatusByProperty] = useState({});
  const [activeId, setActiveId] = useState(null);

  // Load the Maps JS script once for the whole app (id is shared so repeated
  // mounts reuse the same <script>).
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const fetchData = useCallback(async () => {
    try {
      setLoadingProperties(true);
      // Properties carry the coordinates the map needs, so render the pins as
      // soon as they arrive instead of blocking on the inspections read.
      const propsSnap = await getDocs(collection(db, "properties"));
      const props = propsSnap.docs
        .map((d) => ({ _id: d.id, ...d.data() }))
        .filter(hasCoordinates);
      setProperties(props);
      setLoadingProperties(false);

      // Pin colors come from each property's latest inspection status. This
      // read enriches the already-visible map rather than gating it; until it
      // resolves, pins default to "pending" (red).
      const inspectionsSnap = await getDocs(collection(db, "inspections"));
      const latestByProperty = {};
      inspectionsSnap.docs.forEach((d) => {
        const insp = d.data();
        const pid = insp?.propertyId;
        if (!pid) return;
        const prev = latestByProperty[pid];
        if (!prev || (insp?.createdAt || "") > (prev?.createdAt || "")) {
          latestByProperty[pid] = insp;
        }
      });
      const statuses = {};
      Object.keys(latestByProperty).forEach((pid) => {
        statuses[pid] = latestByProperty[pid]?.status || "pending";
      });
      setStatusByProperty(statuses);
    } catch (error) {
      RedNotify(error?.message || "Failed to load properties");
      console.error("Error fetching properties for map:", error);
      setLoadingProperties(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Average of all coordinates — used as the initial center before the map
  // fits to the bounds.
  const center = useMemo(() => {
    if (properties.length === 0) return DEFAULT_CENTER;
    const sum = properties.reduce(
      (acc, t) => ({
        lat: acc.lat + t.coordinates.lat,
        lng: acc.lng + t.coordinates.lng,
      }),
      { lat: 0, lng: 0 },
    );
    return {
      lat: sum.lat / properties.length,
      lng: sum.lng / properties.length,
    };
  }, [properties]);

  // Once the map is ready (and whenever the markers change), fit the viewport
  // to enclose every marker.
  const fitToMarkers = useCallback(
    (map) => {
      if (!map || properties.length === 0 || !window.google) return;
      if (properties.length === 1) {
        map.setCenter(properties[0].coordinates);
        map.setZoom(14);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      properties.forEach((t) => bounds.extend(t.coordinates));
      map.fitBounds(bounds);
    },
    [properties],
  );

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      fitToMarkers(map);
    },
    [fitToMarkers],
  );

  useEffect(() => {
    if (mapRef.current) fitToMarkers(mapRef.current);
  }, [fitToMarkers]);

  const activeProperty = useMemo(
    () => properties.find((t) => t._id === activeId) || null,
    [properties, activeId],
  );

  if (loadError) {
    return (
      <div className="property-map-empty">
        <Empty
          description="Unable to load Google Maps. Check the Maps API key configuration."
        />
      </div>
    );
  }

  if (!isLoaded || loadingProperties) {
    return (
      <div className="property-map-loading">
        <Loader loading />
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="property-map-empty">
        <Empty
          description={
            <span>
              No properties have map coordinates yet.
              <br />
              Properties appear here once a location is set on the property form.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div className="property-map-page">
      <div className="property-map-legend">
        <div className="property-map-legend-row">
          <span
            className="property-map-legend-dot"
            style={{ background: "#ff4d4f" }}
          />
          <span>Pending</span>
        </div>
        <div className="property-map-legend-row">
          <span
            className="property-map-legend-dot"
            style={{ background: "#faad14" }}
          />
          <span>In progress</span>
        </div>
        <div className="property-map-legend-row">
          <span
            className="property-map-legend-dot"
            style={{ background: "#52c41a" }}
          />
          <span>Completed</span>
        </div>
      </div>

      <GoogleMap
        mapContainerClassName="property-map-canvas"
        center={center}
        zoom={DEFAULT_ZOOM}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {properties.map((task) => (
          <Marker
            key={task._id}
            position={task.coordinates}
            icon={markerIcon(statusByProperty[task._id] || "pending")}
            title={task.propertyName || "Property"}
            onClick={() => setActiveId(task._id)}
          />
        ))}

        {activeProperty && (
          <InfoWindow
            position={activeProperty.coordinates}
            onCloseClick={() => setActiveId(null)}
          >
            <div className="property-map-info">
              <div className="property-map-info-title">
                {activeProperty.propertyName || "Unnamed property"}
              </div>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Tag
                  color={statusColor(
                    statusByProperty[activeProperty._id] || "pending",
                  )}
                >
                  {statusLabel(statusByProperty[activeProperty._id] || "pending")}
                </Tag>
                {activeProperty.location && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {activeProperty.location}
                  </Text>
                )}
                <Button
                  type="primary"
                  size="small"
                  block
                  onClick={() => navigate(`/properties/${activeProperty._id}`)}
                >
                  View Property
                </Button>
              </Space>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default PropertyMap;
